import { Children, cloneElement, isValidElement, useEffect, useState } from 'react';
import type { FormEvent, ReactElement, ReactNode } from 'react';
import { statusLabel } from '../format';
import type { ResourceBudget, ResourceStatus } from '../types';

/** Paylaşılan sunum parçaları. Veri çekmez, API bilmez. */

export function StatusPill({ status }: { status: string }) {
  return <span className={`pill pill-${status.toLowerCase()}`}>{statusLabel(status)}</span>;
}

/** Sağlık bir çubuk olarak gösterilir: 0–100 arası bir sayı uzunluk olarak daha hızlı okunur. */
export function HealthBar({ score }: { score: number }) {
  const tone = score >= 70 ? 'good' : score >= 40 ? 'warn' : 'bad';
  return (
    <span className="usage">
      <span className="healthbar" title={`${score}/100`}>
        <span className={`healthbar-fill healthbar-${tone}`} style={{ width: `${score}%` }} />
      </span>
      <span className="usage-text">{score}</span>
    </span>
  );
}

export function Empty({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <p className="empty">
      <strong>{title}</strong>
      {children}
    </p>
  );
}

export function ErrorNote({ message }: { message: string | null }) {
  if (!message) return null;
  return <div className="banner banner-bad">{message}</div>;
}

export function Loading() {
  return <p className="empty">Yükleniyor…</p>;
}

export function Card({
  title,
  actions,
  flush,
  footer,
  children,
}: {
  title?: string;
  actions?: ReactNode;
  flush?: boolean;
  footer?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="card">
      {title && (
        <header className="card-head">
          <h2>{title}</h2>
          {actions}
        </header>
      )}
      <div className={flush ? 'card-body flush' : 'card-body'}>{children}</div>
      {footer && <p className="note">{footer}</p>}
    </section>
  );
}

export function Metric({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: ReactNode;
  hint?: string;
  tone?: 'good' | 'bad';
}) {
  return (
    <div className={`metric${tone ? ` metric-${tone}` : ''}`}>
      <span className="metric-label">{label}</span>
      <span className="metric-value">{value}</span>
      {hint && <span className="metric-hint">{hint}</span>}
    </div>
  );
}

export function Table({ head, children }: { head: string[]; children: ReactNode }) {
  return (
    <div className="table-scroll">
      <table>
        <thead>
          <tr>
            {head.map((column, index) => (
              <th key={`${column}-${index}`}>{column}</th>
            ))}
          </tr>
        </thead>
        <tbody>{Children.map(children, (row) => labelCells(row, head))}</tbody>
      </table>
    </div>
  );
}

/** Her hücreye kendi sütun başlığını `data-label` olarak iliştirir.
 *
 * Dar ekranda tablo satır satır kartlara açılıyor ve orada başlık satırı görünmüyor;
 * hücrenin ne olduğunu yalnızca bu etiket söylüyor. Sayfalarda tek tek yazmak yerine
 * burada türetiyoruz: `head` zaten sütunların tek kaynağı, ikinci bir liste tutmak
 * ilk kaçırılan güncellemede yanlış etikete dönerdi. */
function labelCells(row: ReactNode, head: string[]): ReactNode {
  if (!isValidElement(row)) return row;
  const cells = (row.props as { children?: ReactNode }).children;

  let column = -1;
  const labelled = Children.map(cells, (cell) => {
    if (!isValidElement(cell)) return cell;
    column += 1;
    const label = head[column];
    // Başlıksız sütun (işlem hücresi) etiketsiz kalır; kartta zaten düğme olarak okunur.
    if (cell.type !== 'td' || !label) return cell;
    return cloneElement(cell as ReactElement<Record<string, unknown>>, { 'data-label': label });
  });

  return cloneElement(row as ReactElement<Record<string, unknown>>, undefined, labelled);
}

/** Açık/kapalı için kaydırmalı anahtar.
 *
 * `role="switch"` ile işaretli: ekran okuyucu bunu iki durumlu bir anahtar olarak
 * duyurur, "Aç" yazan bir düğme olarak değil — düğme metni eylemi söyler ama mevcut
 * durumu söylemez, anahtarın kendisi ikisini birden taşır. */
export function Toggle({
  on,
  label,
  onChange,
  disabled = false,
}: {
  on: boolean;
  label: string;
  onChange: (next: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      title={on ? `${label}: açık` : `${label}: kapalı`}
      className={`toggle${on ? ' on' : ''}`}
      disabled={disabled}
      onClick={() => onChange(!on)}
    >
      <span className="toggle-track" aria-hidden="true">
        <span className="toggle-knob" />
      </span>
      <span className="toggle-text">{on ? 'Açık' : 'Kapalı'}</span>
    </button>
  );
}

export function Modal({
  title,
  onClose,
  onSubmit,
  submitLabel = 'Kaydet',
  busy,
  error,
  /** Gömülü tarayıcı gibi, 540px'e sığmayan içerik için. */
  wide,
  children,
}: {
  title: string;
  onClose: () => void;
  onSubmit: () => void;
  submitLabel?: string;
  busy?: boolean;
  error?: string | null;
  wide?: boolean;
  children: ReactNode;
}) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    onSubmit();
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <form
        className={wide ? 'modal wide' : 'modal'}
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
      >
        <h3>{title}</h3>
        {children}
        <ErrorNote message={error ?? null} />
        <footer className="modal-actions">
          <button type="button" className="ghost" onClick={onClose}>
            Vazgeç
          </button>
          <button type="submit" disabled={busy}>
            {busy ? 'Çalışıyor…' : submitLabel}
          </button>
        </footer>
      </form>
    </div>
  );
}

export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <label className="field">
      <span className="field-label">{label}</span>
      {children}
      {hint && <span className="field-hint">{hint}</span>}
    </label>
  );
}

/**
 * Geri alınamayan bir işlemden önce soran düğme.
 *
 * Bilerek iki tıklama: anahtar iptal etmek ya da havuz silmek düşünülerek yapılmalı, ama
 * o kadar da tören olmamalı ki operatör uyarıyı okumayı bıraksın.
 */
export function ConfirmButton({
  label,
  confirmLabel,
  onConfirm,
  className = 'ghost danger tiny',
}: {
  label: string;
  confirmLabel: string;
  onConfirm: () => void;
  className?: string;
}) {
  const [armed, setArmed] = useState(false);

  useEffect(() => {
    if (!armed) return;
    const timer = setTimeout(() => setArmed(false), 4000);
    return () => clearTimeout(timer);
  }, [armed]);

  return (
    <button
      type="button"
      className={className}
      onClick={() => (armed ? onConfirm() : setArmed(true))}
    >
      {armed ? confirmLabel : label}
    </button>
  );
}

/** Bütçeyi okunur bir satıra çevirir, ya da tavan olmadığını söyler. */
export function BudgetLine({ budget }: { budget: ResourceBudget }) {
  const parts: string[] = [];
  if (budget.maxRunsPerHour) parts.push(`${budget.maxRunsPerHour} çalışma/sa`);
  if (budget.maxRunsPerDay) parts.push(`${budget.maxRunsPerDay} çalışma/gün`);
  if (budget.maxUpstreamRequestsPerHour)
    parts.push(`${budget.maxUpstreamRequestsPerHour} istek/sa`);
  if (budget.maxUpstreamRequestsPerDay) parts.push(`${budget.maxUpstreamRequestsPerDay} istek/gün`);
  return <span className="muted small">{parts.length > 0 ? parts.join(' · ') : 'sınırsız'}</span>;
}

/** Bütçeye karşı kullanım: operatörün gerçekten aksiyon aldığı sayı. */
export function UsageBar({ used, limit }: { used: number; limit?: number }) {
  if (!limit) return <span className="muted">{used}</span>;
  const pct = Math.min(100, Math.round((used / limit) * 100));
  const tone = pct >= 90 ? 'bad' : pct >= 70 ? 'warn' : 'good';
  return (
    <span className="usage">
      <span className="healthbar" title={`${limit} içinde ${used}`}>
        <span className={`healthbar-fill healthbar-${tone}`} style={{ width: `${pct}%` }} />
      </span>
      <span className="usage-text">
        {used}/{limit}
      </span>
    </span>
  );
}

export const RESOURCE_STATUSES: ResourceStatus[] = [
  'ACTIVE',
  'DEGRADED',
  'COOLDOWN',
  'CHALLENGED',
  'DISABLED',
  'DEAD',
];
